// Vendor libraries for the Studio bundle (layouts/application.html.haml).
//
// This replaces the RubyGems + "Vendored JavaScript files" require lists from
// app/assets/javascripts/application.js.coffee.erb. Libraries that ship as npm
// packages are imported here; libraries that only exist as checked-in files
// under rails/vendor/assets/javascripts are imported through the `@vendor`
// alias (same file Sprockets still uses, so nothing is duplicated or moved
// until Sprockets is deleted in Phase 7).
//
// The few genuinely-global objects the legacy CoffeeScript app and the React
// components reach for (Highcharts, I18n, moment, hugerte, _) are published on
// `window`, matching the old Sprockets behavior. jQuery plugins attach to the
// shared jQuery instance via ProvidePlugin and ./jquery.
//
// Deferred to Phase 4 (they need the Rails codegen or the app JS that consumes
// them, which is migrated then): the i18n `translations` blob and `onload`.

// jQuery first — publishes window.$ / window.jQuery for the plugins below.
import "./jquery";

// Core library globals (underscore, moment, i18n-js) as a side-effect import so
// they are on window BEFORE the vendored plugins below evaluate — daterangepicker
// reads window.moment at load time. See ./core_globals.
import "./core_globals";

import hugerte from "hugerte";
// hugerte splits its default theme and icon pack into separate entry points that
// must be imported for a self-hosted build.
import "hugerte/themes/silver";
import "hugerte/icons/default";
// The DOM logic lives in a separately-imported "model" (a TinyMCE 6 split that
// HugeRTE keeps); a self-hosted build must register it or init fails with "no
// model registered".
import "hugerte/models/dom";
// The UI and iframe content skins are bundled so HugeRTE doesn't try to fetch
// them from a baseURL that doesn't resolve under webpack.
import "hugerte/skins/ui/oxide/skin.min.css";
import "hugerte/skins/content/default/content";
// Bundle every plugin the config/hugerte.yml presets reference, so HugeRTE finds
// them registered instead of dynamically loading plugin.js from a baseURL (which
// 404'd and crashed init with "Cannot read properties of undefined (reading
// 'apply')"). The custom `subscriberquotaplugin` registers itself from
// js/legacy/concerns/hugerte_subscriberquotaplugin.js.coffee.
//
// contextmenu, hr, paste, and textcolor are built into core, and fullpage does
// not exist (full-document editing is re-implemented by the custom
// fullpageemulation plugin in legacy/concerns). `lists` is a required
// dependency of advlist and the bullist/numlist toolbar buttons, so it must be
// imported explicitly.
import "hugerte/plugins/advlist";
import "hugerte/plugins/charmap";
import "hugerte/plugins/code";
import "hugerte/plugins/fullscreen";
import "hugerte/plugins/image";
import "hugerte/plugins/insertdatetime";
import "hugerte/plugins/link";
import "hugerte/plugins/lists";
import "hugerte/plugins/nonbreaking";
import "hugerte/plugins/pagebreak";
import "hugerte/plugins/preview";
import "hugerte/plugins/searchreplace";
import "hugerte/plugins/table";
import "hugerte/plugins/template";
import "hugerte/plugins/visualblocks";
import "hugerte/plugins/wordcount";

// NOTE: HugeRTE is the MIT-licensed community fork of TinyMCE 6 (TinyMCE 7+ is
// GPL-2.0-or-later/commercial, and 8.x additionally gates the editor behind a
// validated `license_key`). Every plugin imported above ships in the MIT
// package, so no license validation is involved.

window.hugerte = hugerte;

// Full-document (fullpage) emulation, published on window so the custom
// fullpageemulation plugin (a legacy CoffeeScript file, which reaches globals
// the same way it reaches window.hugerte) can use it. See
// helpers/full_document.js and legacy/concerns/hugerte_fullpageemulationplugin.js.coffee.
import {
  fullDocumentBody,
  splitFullDocument,
  wrapFullDocument,
} from "@js/helpers/full_document.js";
window.FullDocument = { fullDocumentBody, splitFullDocument, wrapFullDocument };

// select2 v4 attaches $.fn.select2 to the global jQuery published above.
import "select2";

// Rails UJS (checked-in copy, pinned to the jQuery-3 compatible build).
import "@vendor/jquery_ujs";

// onload_js: publishes window.runOnLoad, which ~20 studio controllers use to run
// per-controller/action page setup. The old Sprockets manifest did
// `#= require onload`; that directive was dropped in the webpack migration.
import "@vendor/onload";

// Charting (checked-in Highcharts 8.2.2 — kept at this exact version).
import Highcharts from "@vendor/highcharts-8.2.2/highcharts";
window.Highcharts = Highcharts;

// Date/time UI.
import "@vendor/daterangepicker";
import "@vendor/bootstrap-datepicker";

// Bootstrap 2.x JS (checked-in).
import "@vendor/bootstrap";

// Misc vendored jQuery plugins and utilities (attach to the shared jQuery).
import "@vendor/jquery.nav";
import "@vendor/jquery.scrollTo";
import "@vendor/jquery.stickem";
import "@vendor/jquery.hoverIntent";
import "@vendor/jquery.ocupload";
import "@vendor/jquery.menu-aim";
import "@vendor/jQuery.XDomainRequest";

// Standalone utility libraries.
import "@vendor/modernizr-2.6.2.min";
import "@vendor/base64";
import "@vendor/strftime";
import "@vendor/purl";
// spin.js's UMD takes its CommonJS branch under webpack, so it never assigns
// window.Spinner the way it did under Sprockets. The legacy images browser
// calls `new Spinner()` as a global, so re-expose it explicitly.
import Spinner from "@vendor/spin";
window.Spinner = Spinner;
import "@vendor/highlight.pack";
import "@vendor/FileAPI.min";
import "@vendor/zero-clipboard";
import "@vendor/v.application";

// ACE editor (special_sending_rules code editor). The old application.coffee
// manifest listed these as `#= require ace/…`; those Sprockets directives were
// not translated to imports, so window.ace and the $.fn.ace jQuery plugin were
// missing ("e.ace is not a function"). Import the core, the modes/themes the app
// uses, and the jquery-ace wrapper. (Workers are disabled via setUseWorker(false)
// at the call site, so no dynamic worker loading is needed.)
import "@vendor/ace/ace";
import "@vendor/ace/mode-perl";
import "@vendor/ace/mode-php";
import "@vendor/ace/mode-ruby";
import "@vendor/ace/mode-javascript";
import "@vendor/ace/mode-sql";
import "@vendor/ace/theme-xcode";
import "@vendor/ace/jquery-ace";
